import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnInit,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import {
  IMAGE_SRC,
  STATUS_OPTIONS,
  STOP_TYPE_OPTIONS,
  STOP_TYPES,
} from '../../../shared-constants';
import {
  IDO_ICONS,
  IDO_ICONS_WITH_WARNING,
} from './table-dropdown-status.constants';

@Component({
  selector: 'app-table-dropdown-status',
  templateUrl: './table-dropdown-status.component.html',
  styleUrls: ['./table-dropdown-status.component.scss'],
  standalone: false,
})
export class TableDropdownStatusComponent implements OnInit {
  private readonly elementRef = inject(ElementRef);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.showDropdown &&
      !this.elementRef.nativeElement.contains(event.target)
    ) {
      this.showDropdown = false;
      this.dropdownChange.emit('');
    }
  }

  @Input({ required: true }) header!: string;
  @Input({ required: true }) data!: TableColumn;
  @Input({ required: true }) resetData!: BehaviorSubject<boolean>;
  @Input({ required: true }) showDropdown!: boolean;
  @Input() hasWarningSymbol = false;
  @Input() dropdownWidth = 100;
  @Input() rightAlign = false;
  @Input() operationsIcons = false;
  emitStatusChange = output<string | boolean | unknown[]>();
  dropdownChange = output<string>();

  destroy = inject(DestroyRef);

  readonly ImageOptions = STATUS_OPTIONS;
  readonly StopTypeImageOptions = STOP_TYPE_OPTIONS;
  readonly Images = IMAGE_SRC;
  readonly SELECT_ALL = STATUS_OPTIONS.ALL_SELECTED;

  allOptionsSelected = true;
  displayedData = this.ImageOptions.ALL_SELECTED;
  statusOptions: Items[] = [...IDO_ICONS];
  stopTypeOptions: Items[] = [...STOP_TYPES];
  checkedData!: Items[];

  get dropdownWidthClass(): string | null {
    const allowedWidths = [60, 72, 100];
    return allowedWidths.includes(this.dropdownWidth)
      ? `dropdown-width-${this.dropdownWidth}`
      : 'dropdown-width-100';
  }

  ngOnInit(): void {
    this.resetData
      .asObservable()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((value) => value),
      )
      .subscribe(() => this.resetValues());

    //Take a Deep Copy of the data so it remains distinct if multiple status' on a table
    this.statusOptions = this.hasWarningSymbol
      ? JSON.parse(JSON.stringify(IDO_ICONS_WITH_WARNING))
      : JSON.parse(JSON.stringify(IDO_ICONS));
  }

  onAllSelect() {
    setTimeout(() => {
      this.selectAllForCheckedColumn();
      this.statusChange();
    });
  }

  statusChange() {
    setTimeout(() => {
      this.handleCheckBoxSelection();
      this.displayedData = this.handleDropdownText();
      this.checkedData = !this.operationsIcons
        ? this.statusOptions.filter((value) => value.selected)
        : this.stopTypeOptions.filter((value) => value.selected);
      this.emitStatusChange.emit(this.transformData());
    });
  }

  updateDropdown() {
    this.dropdownChange.emit('');
  }

  get toolTipText() {
    const filteredData = this.statusOptions?.filter(
      (option) => option.selected,
    );
    return filteredData
      ?.map((option) => {
        if (this.operationsIcons) {
          switch (option.id) {
            case STATUS_OPTIONS.ACTIVE:
              return STOP_TYPE_OPTIONS.ON_DEMAND;
            case STATUS_OPTIONS.DEACTIVATED:
              return STOP_TYPE_OPTIONS.SCHEDULED;
            default:
              return null;
          }
        } else {
          return option.id;
        }
      })
      .join(', ');
  }

  get showTooltip() {
    const filteredData = this.statusOptions?.filter(
      (option) => option.selected,
    );
    return filteredData?.length > 1;
  }

  private transformData() {
    if (!this.operationsIcons) {
      switch (true) {
        case this.allOptionsSelected:
          return '';
        case this.checkedData.length === 0:
          return 'n/a';
        default:
          return this.hasWarningSymbol
            ? this.checkedData.map((value) => value.id)
            : this.checkedData[0].id === STATUS_OPTIONS.ACTIVE;
      }
    } else {
      switch (true) {
        case this.allOptionsSelected:
          return '';
        case this.checkedData.length === 0:
          return 'n/a';
        default:
          return this.checkedData.map((value) => value.id);
      }
    }
  }

  private handleDropdownText() {
    if (!this.operationsIcons) {
      const filteredData = this.statusOptions.filter(
        (option) => option.selected,
      );
      switch (true) {
        case filteredData.length == 0:
          return '';
        case filteredData.length == 1:
          return filteredData.map((option) => option.id)[0];
        case filteredData.length === this.statusOptions.length:
          return STATUS_OPTIONS.ALL_SELECTED;
        default:
          return STATUS_OPTIONS.SOME_SELECTED;
      }
    } else {
      const filteredData = this.stopTypeOptions.filter(
        (option) => option.selected,
      );
      switch (true) {
        case filteredData.length === this.stopTypeOptions.length:
          return STOP_TYPE_OPTIONS.ALL_SELECTED;
        case filteredData.length == 1:
          return filteredData.map((option) => option.id)[0];
        case filteredData.length == 0:
          return '';
        default:
          return STOP_TYPE_OPTIONS.SOME_SELECTED;
      }
    }
  }

  private handleCheckBoxSelection(): void {
    let selected = 0;

    if (this.operationsIcons) {
      this.stopTypeOptions.forEach((el) => {
        if (el.selected) {
          selected++;
        }
      });
      this.allOptionsSelected = selected === this.stopTypeOptions.length;
    } else {
      this.statusOptions.forEach((el) => {
        if (el.selected) {
          selected++;
        }
      });
      this.allOptionsSelected = selected === this.statusOptions.length;
    }
  }

  private selectAllForCheckedColumn(): void {
    this.statusOptions.forEach((item) => {
      item.selected = this.allOptionsSelected;
    });

    if (this.operationsIcons) {
      this.stopTypeOptions.forEach((item) => {
        item.selected = this.allOptionsSelected;
      });
    }
  }

  private resetValues() {
    this.statusOptions.forEach((value) => {
      value.selected = true;
    });
    this.stopTypeOptions.forEach((value) => {
      value.selected = true;
    });
    this.allOptionsSelected = true;
    this.displayedData = this.ImageOptions.ALL_SELECTED;
    this.data.ids = [];
    this.showDropdown = false;
  }
}
